import { Diamond } from "lucide-react";
import {
  Alert,
  Button,
  FormStack,
  Input,
  Select,
  Tooltip,
} from "@marekl/adminer/components";
import { CodeMirrorField } from "../../components/CodeMirrorField";
import {
  BLOCK_DEFS,
  isBinding,
  type EditorBlock,
  type PropDef,
  type PropValue,
} from "./blocksModel";
import styles from "./TemplateDetailPage.module.css";

const BIND_HINT =
  "Dot path into the sample data (e.g. user.name). Inside a repeat it resolves against the current item (e.g. title).";

interface PropFieldProps {
  def: PropDef;
  value: PropValue | undefined;
  onChange: (value: PropValue | undefined) => void;
}

function PropField({ def, value, onChange }: PropFieldProps) {
  const bound = isBinding(value);

  const toggle = def.bindable ? (
    <Tooltip
      content={
        bound
          ? "Bound to data — switch to a fixed value"
          : "Fixed value — switch to a data binding"
      }
    >
      <Button
        size="sm"
        iconOnly
        pressed={bound}
        aria-label={`Toggle binding of field ${def.label}`}
        startIcon={<Diamond size={14} />}
        onClick={() => onChange(bound ? "" : { bind: "" })}
      />
    </Tooltip>
  ) : null;

  if (bound) {
    return (
      <Input
        label={`${def.label} — binding`}
        value={value.bind}
        onChange={(e) => onChange({ bind: e.target.value })}
        placeholder="user.name"
        hint={BIND_HINT}
        spellCheck={false}
        suffix={toggle}
      />
    );
  }

  switch (def.kind) {
    case "select":
      return (
        <Select
          label={def.label}
          hint={def.hint}
          value={value === undefined ? "" : String(value)}
          onChange={(e) => {
            const opt = def.options?.find((o) => String(o.value) === e.target.value);
            onChange(opt ? opt.value : e.target.value);
          }}
        >
          {def.options?.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    case "number":
      return (
        <Input
          label={def.label}
          type="number"
          hint={def.hint}
          value={value === undefined ? "" : String(value)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(undefined);
              return;
            }
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : raw);
          }}
          suffix={toggle}
        />
      );
    case "html":
      return (
        <div className={styles.htmlPropField}>
          <CodeMirrorField
            label={def.label}
            value={typeof value === "string" ? value : ""}
            onChange={(v) => onChange(v)}
            height="140px"
            hint={def.hint}
          />
          {toggle && <div className={styles.htmlPropToggle}>{toggle}</div>}
        </div>
      );
    default:
      return (
        <Input
          label={def.label}
          hint={def.hint}
          placeholder={def.placeholder}
          value={value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          suffix={toggle}
        />
      );
  }
}

interface BlockPropsFormProps {
  block: EditorBlock;
  onChange: (block: EditorBlock) => void;
}

/**
 * Property form for the selected block. Every bindable prop carries the ◇
 * toggle switching between a literal value and a `{bind: "path"}` binding.
 */
export function BlockPropsForm({ block, onChange }: BlockPropsFormProps) {
  const def = BLOCK_DEFS[block.type];
  if (!def) return null;

  function setProp(name: string, value: PropValue | undefined) {
    const props = { ...block.props };
    if (value === undefined) {
      delete props[name];
    } else {
      props[name] = value;
    }
    onChange({ ...block, props });
  }

  if (def.props.length === 0) {
    return (
      <p className={styles.noProps}>This block has no properties.</p>
    );
  }

  return (
    <FormStack>
      {def.advanced && (
        <Alert variant="warn">
          Advanced users only — the content is inserted into the email verbatim.
        </Alert>
      )}
      {def.props.map((p) => (
        <PropField
          key={p.name}
          def={p}
          value={block.props[p.name]}
          onChange={(v) => setProp(p.name, v)}
        />
      ))}
    </FormStack>
  );
}
